import { useCallback } from 'react';
import { RoiId, StructureSetId } from '~/domain/changes/ids';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { DomainError } from '~/domain/errors/DomainError';
import { Roi } from '~/domain/patient/roi';
import { enqueueSnackbar } from '~/utils/notifications';
import { useStructureSetService } from './useStructureSetService';

export function useStructureSetCommands() {
    const service = useStructureSetService();

    // The unsaved-changes guard arms itself: the service emits a
    // changeset, PendingChangesStore accumulates it, and
    // PendingChangesRecoilBridge derives hasUnsavedChangesState from it.
    const wrap = useCallback(<T>(op: () => T): T | undefined => {
        try {
            return op();
        } catch (e) {
            if (e instanceof DomainError) {
                enqueueSnackbar(e.message, { variant: 'warning' });
                return undefined;
            }
            throw e;
        }
    }, []);

    const approveAll = useCallback(
        (id: StructureSetId, reviewer: string): void => {
            wrap(() => service.approveAll({ id }, reviewer));
        },
        [service, wrap]
    );

    const createRoi = useCallback(
        (
            id: StructureSetId,
            type: RoiInterpretedType,
            name: string
        ): Roi | undefined => wrap(() => service.createRoi({ id }, type, name)),
        [service, wrap]
    );

    const removeRoi = useCallback(
        (id: RoiId): void => {
            wrap(() => service.removeRoi({ id }));
        },
        [service, wrap]
    );

    /**
     * Deletes several ROIs of one structure set. Returns whether the deletion
     * actually happened: {@link StructureSetService.removeRois} is
     * all-or-nothing and {@link wrap} swallows a DomainError into a snackbar,
     * so callers that also drop UI state (selection, active ROI) MUST gate that
     * on the returned flag — otherwise an aborted delete leaves the structures
     * present but the user's selection gone, with no way to retry but to
     * re-pick every structure.
     */
    const removeRois = useCallback(
        (id: StructureSetId, roiIds: readonly RoiId[]): boolean =>
            wrap(() => {
                service.removeRois({ id }, roiIds);
                return true;
            }) ?? false,
        [service, wrap]
    );

    return { approveAll, createRoi, removeRoi, removeRois };
}
